/**
 * 実績紹介シングルページ 背景テキストスライドモジュール
 * 
 * @package LIVMO
 * @version 1.0.0
 */

class ArchiveSingleTextSlide {
  constructor() {
    this.slider = document.querySelector('.archive-single-hero__textSlide');
    this.swiper = null;
    this.wrapper = null;
    this.scrollPosition = 0;
    this.baseScrollSpeed = 1.0;
    this.targetSpeed = 1.0;
    this.currentSpeed = 1.0;
    this.animationFrameId = null;
    this.isScrolling = false;
    
    // スクロール速度計算用
    this.lastScrollY = window.scrollY;
    this.lastScrollTime = Date.now();
    this.scrollVelocity = 0;
    this.maxScrollSpeed = 5.0;
    this.scrollVelocityThreshold = 50;
    this.scrollDirection = 1;
    
    // イージング設定
    this.easingFactor = 0.08;
    
    if (this.slider) {
      this.init();
    }
  }
  
  /**
   * 初期化
   */
  async init() {
    if (!this.slider) {
      return;
    }
    
    // Swiperが既に読み込まれているか確認、なければ読み込む
    if (typeof Swiper === 'undefined') {
      await this.loadSwiper();
    }
    
    this.initSwiper();
  }
  
  /**
   * Swiperライブラリの動的読み込み
   */
  async loadSwiper() {
    return new Promise((resolve, reject) => {
      if (typeof Swiper !== 'undefined') {
        resolve();
        return;
      }
      
      const swiperScript = document.createElement('script');
      swiperScript.src = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';
      swiperScript.async = true;
      
      const swiperCSS = document.createElement('link');
      swiperCSS.rel = 'stylesheet';
      swiperCSS.href = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';
      
      const cssPromise = new Promise((res) => {
        swiperCSS.onload = () => res();
        swiperCSS.onerror = () => res();
      });
      document.head.appendChild(swiperCSS);
      
      swiperScript.onload = () => {
        resolve();
      };
      swiperScript.onerror = () => {
        reject();
      };
      document.head.appendChild(swiperScript);
    });
  }
  
  /**
   * Swiper初期化
   */
  initSwiper() {
    if (typeof Swiper === 'undefined') {
      return;
    }
    
    if (!this.slider) {
      return;
    }
    
    
    const wrapper = this.slider.querySelector('.swiper-wrapper');
    if (!wrapper) {
      return;
    }
    this.wrapper = wrapper;
    
    const config = {
      slidesPerView: 'auto',
      spaceBetween: 100,
      loop: false,
      speed: 0,
      allowTouchMove: false,
      freeMode: {
        enabled: false,
      },
      on: {
        init: () => {
          setTimeout(() => {
            this.startContinuousScroll();
          }, 200);
        },
      }
    };

    this.swiper = new Swiper(this.slider, config);
    this.initScrollSpeedControl();
  }
  
  /**
   * スクロール速度制御の初期化
   */
  initScrollSpeedControl() {
    let ticking = false;
    
    const updateScrollSpeed = () => {
      // Lenisが利用可能な場合はLenisから取得、そうでなければ通常の方法
      const currentScrollY = (window.lenis && window.lenis.isInitialized) 
        ? window.lenis.scroll 
        : window.scrollY;
      const currentTime = Date.now();
      const timeDelta = currentTime - this.lastScrollTime;
      
      if (timeDelta > 0) {
        const scrollDeltaY = currentScrollY - this.lastScrollY;
        
        if (Math.abs(scrollDeltaY) > 1) {
          this.scrollDirection = scrollDeltaY > 0 ? 1 : -1;
        }
        
        const scrollDelta = Math.abs(scrollDeltaY);
        const scrollVelocity = scrollDelta / (timeDelta / 1000);
        
        const normalizedVelocity = Math.min(scrollVelocity / this.scrollVelocityThreshold, 1);
        const easedVelocity = normalizedVelocity * normalizedVelocity;
        const speedMultiplier = 1 + easedVelocity * (this.maxScrollSpeed - 1);
        
        if (scrollVelocity < 10) {
          this.targetSpeed = this.baseScrollSpeed * this.scrollDirection;
        } else {
          this.targetSpeed = this.baseScrollSpeed * speedMultiplier * this.scrollDirection;
        }
        
        this.lastScrollY = currentScrollY;
        this.lastScrollTime = currentTime;
      }
      
      ticking = false;
    };
    
    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(updateScrollSpeed);
        ticking = true;
      }
    }, { passive: true });
    
    setInterval(() => {
      const timeSinceLastScroll = Date.now() - this.lastScrollTime;
      if (timeSinceLastScroll > 100) {
        this.targetSpeed = this.baseScrollSpeed * this.scrollDirection;
      }
    }, 50);
  }
  
  /**
   * 連続スクロールを開始
   */
  startContinuousScroll() {
    
    if (!this.wrapper) {
      return;
    }
    
    const slides = this.wrapper.querySelectorAll('.swiper-slide');
    
    if (!slides || slides.length === 0) {
      return;
    }
    
    let totalWidth = 0;
    slides.forEach((slide, index) => {
      const width = slide.offsetWidth || slide.getBoundingClientRect().width;
      totalWidth += width + 100;
    });
    
    
    if (totalWidth === 0) {
      return;
    }
    
    slides.forEach(slide => {
      const clone = slide.cloneNode(true);
      this.wrapper.appendChild(clone);
    });
    
    this.isScrolling = true;
    this.scrollPosition = 0;
    
    
    const animate = () => {
      if (!this.isScrolling || !this.wrapper) {
        return;
      }
      
      const speedDiff = this.targetSpeed - this.currentSpeed;
      this.currentSpeed += speedDiff * this.easingFactor;
      
      if (Math.abs(speedDiff) < 0.01) {
        this.currentSpeed = this.targetSpeed;
      }
      
      this.scrollPosition -= this.currentSpeed;
      
      if (this.currentSpeed > 0 && totalWidth > 0 && Math.abs(this.scrollPosition) >= totalWidth) {
        this.scrollPosition = 0;
      }
      if (this.currentSpeed < 0 && totalWidth > 0) {
        if (this.scrollPosition >= 0) {
          this.scrollPosition = -totalWidth;
        }
        if (this.scrollPosition < -totalWidth) {
          this.scrollPosition = 0;
        }
      }
      
      this.wrapper.style.transform = `translate3d(${this.scrollPosition}px, 0, 0)`;
      this.wrapper.style.transition = 'none';
      
      this.animationFrameId = requestAnimationFrame(animate);
    };
    
    this.animationFrameId = requestAnimationFrame(animate);
  }
  
  /**
   * スクロールを停止
   */
  stopContinuousScroll() {
    this.isScrolling = false;
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }
  
  /**
   * 破棄
   */
  destroy() {
    this.stopContinuousScroll();
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    this.wrapper = null;
  }
}

// DOMContentLoadedで初期化
function bootArchiveSingleTextSlide() {
  if (document.querySelector('.archive-single-hero__textSlide')) {
    if (!window.__archiveSingleTextSlideInstance) {
      window.__archiveSingleTextSlideInstance = new ArchiveSingleTextSlide();
    }
  }
}

document.addEventListener('DOMContentLoaded', bootArchiveSingleTextSlide);

export default ArchiveSingleTextSlide;

